import { components as c } from "@/components";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { diaryByIdQueryOptions } from "@/features/diaries";
import { InjectionSchemaT, InjectionSchemaZ } from "@/features/injection/types/injectionSchema";
import { useInjectionMutation } from "@/features/injection/api/upsertInjection";
import { useReplaceInjectionDetails } from "@/features/injection/api/useReplaceInjectionDetails";
import {
    formatInjectionDetailsDefaultValues,
    InjectionDetailSchemaT,
    InjectionDetailsZ,
} from "@/features/injection/components/InjectionDetailsForm";
import { extractEventById } from "@/features/diaries/services/extractEventById";
import InjectionFormFields from "@/features/injection/components/InjectionFormFields";
import InjectionDetailsFormFields from "@/features/injection/components/InjectionDetailsFormFields";
import { FormErrors } from "@patient-watch/forms";
import { ui } from "@patient-watch/ui";
import { useAuth } from "@/utils/auth";
import { z } from "zod";
import { useState, useEffect } from "react";
import { insertEvent } from "@/features/events/api/insertEvent";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";

const searchSchema = updateDiaryFlowSearchSchema.extend({
    event_type: z.literal("injection"),
});

type Stage = "clinicians" | "details";

const AddInjectionPage = () => {
    const { diaryId, eventId, trackerId } = Route.useParams();
    const search = Route.useSearch();
    const { type } = search;
    const { diary, injection } = Route.useLoaderData();
    const auth = useAuth();
    const navigate = useNavigate();
    const [stage, setStage] = useState<Stage>(() => (injection ? "details" : "clinicians"));

    // Generate injection ID
    const injectionId = injection?.injection_id ?? crypto.randomUUID();

    const injectionMutation = useInjectionMutation({ diaryId });
    const replaceInjectionDetails = useReplaceInjectionDetails({ diaryId });

    // Clinicians form
    const cliniciansForm = useForm<InjectionSchemaT>({
        resolver: zodResolver(InjectionSchemaZ),
        defaultValues: {
            event_id: eventId,
            injection_id: injectionId,
            referring_clinician_id: injection?.referring_clinician_id ?? search.referring_clinician_id ?? auth.id,
            injection_method_id: injection?.injection_method_id ?? search.injection_method_id ?? undefined,
            injecting_clinician_id: injection?.injecting_clinician_id ?? search.injecting_clinician_id ?? auth.id,
        },
    });

    // Details form
    const detailsForm = useForm<InjectionDetailSchemaT>({
        resolver: zodResolver(InjectionDetailsZ),
        defaultValues: formatInjectionDetailsDefaultValues(injectionId, injection),
    });

    useEffect(() => {
        if (injection) {
            detailsForm.reset(formatInjectionDetailsDefaultValues(injectionId, injection));
        }
    }, [injection, injectionId, detailsForm]);

    const handleCliniciansSubmit: SubmitHandler<InjectionSchemaT> = async (formData) => {
        // First, ensure the event exists
        await insertEvent({ event_id: eventId, diary_id: diaryId });
        // Then insert the injection
        injectionMutation.mutate(formData, {
            onSuccess: () => setStage("details"),
        });
    };

    const handleDetailsSubmit: SubmitHandler<InjectionDetailSchemaT> = async (formData) => {
        replaceInjectionDetails.mutate(
            {
                injection_details: formData.injection_details.map((detail) => ({
                    ...detail,
                    side: !detail.side ? null : detail.side,
                })),
                injection_id: injectionId,
            },
            {
                onSuccess: () => {
                    if (search.from === "worklist") {
                        void navigate({ to: "/worklist" });
                        return;
                    }
                    void navigate({
                        to: "/patients/$diaryId/update/$eventId/$trackerId/date",
                        params: { diaryId, eventId, trackerId },
                        search: { ...search, type },
                    });
                },
            }
        );
    };

    if (stage === "clinicians") {
        return (
            <ui.Form {...cliniciansForm}>
                <form onSubmit={cliniciansForm.handleSubmit(handleCliniciansSubmit)} className="space-y-6">
                    <h2 className="font-semibold text-lg">Injection Details</h2>
                    <InjectionFormFields organisationId={diary?.organisation_id as string} />
                    <fc.ContinueButton buttonType="submit" size="lg" className="w-full" />
                </form>
            </ui.Form>
        );
    }

    return (
        <c.ui.Form {...detailsForm}>
            <form onSubmit={detailsForm.handleSubmit(handleDetailsSubmit)} className="space-y-6">
                <h2 className="font-semibold text-lg">What was injected?</h2>
                <InjectionDetailsFormFields injectionId={injectionId} />
                <FormErrors />
                <fc.NextButton buttonType="submit" size="lg" className="w-full" />
            </form>
        </c.ui.Form>
    );
};

export const Route = createFileRoute(
    "/_app/patients/$diaryId/update/$eventId/$trackerId/intervention-details/injection"
)({
    component: AddInjectionPage,
    validateSearch: searchSchema,
    loader: async ({ context, params }) => {
        const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        // Event may not exist yet in the diary since we create it on form submit
        const event = extractEventById(diary!, params.eventId);
        const injection = event?.injections_view?.[0] ?? undefined;
        return { diary, event, injection };
    },
});
